import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Tooltip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Chip,
} from '@mui/material';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import KeyboardOutlinedIcon from '@mui/icons-material/KeyboardOutlined';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import { QRCodeSVG } from 'qrcode.react';
import { Status, Wrapper } from '@googlemaps/react-wrapper';
import { useNotify } from 'react-admin';

type Coordinate = { latitude: number; longitude: number };

interface UniqueCodeRuleCardProps {
    code?: string | null;
    /** Código da rede (subdomínio) usado para montar o universal link. */
    chainCode?: string | null;
    giftId?: string;
    useManualCode?: boolean;
    useQrCode?: boolean;
    requireLocation?: boolean;
    locationCoordinates?: Coordinate[] | null;
}

const GREEN = '#2e7d32';
const GREEN_LIGHT = '#e8f5e9';

/**
 * Monta o universal link do brinde, no mesmo padrão do convite de grupo de
 * clientes (EditCustomerGroup): `https://{chainCode}-app.pontuax.com.br/?giftId={id}`,
 * trocando `customerGroupId` por `giftId`. Quando a regra exige conferência de
 * local, anexa `&requireLocation=true` — o app encaminha esses query params ao
 * /attribution, então o resgate já entra ciente do geofence.
 */
export const buildGiftUniversalLink = (
    chainCode?: string | null,
    giftId?: string,
    requireLocation?: boolean,
): string => {
    if (!chainCode || !giftId) return '';
    const base = `https://${chainCode}-app.pontuax.com.br/?giftId=${giftId}`;
    return requireLocation ? `${base}&requireLocation=true` : base;
};

/**
 * Mapa read-only do polígono de geofence (mesmo estilo da aba GPS do posto,
 * mas com o polígono em verde para combinar com o card). Usado tanto no
 * mini preview quanto no dialog "Ver".
 */
const ReadOnlyPolygonMap: React.FC<{ coordinates: Coordinate[]; height: number }> = ({ coordinates, height }) => {
    const mapRef = useRef<HTMLDivElement | null>(null);
    const mapInstanceRef = useRef<any>(null);
    const polygonRef = useRef<any>(null);

    useEffect(() => {
        if (!mapRef.current || mapInstanceRef.current || !coordinates?.length) return;

        const paths = coordinates.map((c) => ({ lat: c.latitude, lng: c.longitude }));
        const bounds = new google.maps.LatLngBounds();
        paths.forEach((p) => bounds.extend(p));
        const center = bounds.getCenter();

        const map = new google.maps.Map(mapRef.current, {
            center: { lat: center.lat(), lng: center.lng() },
            zoom: 18,
            disableDefaultUI: true,
            gestureHandling: 'cooperative',
            mapId: (import.meta as any).env?.VITE_MAP_ID || 'd06eb9fab6b219cd',
        });
        mapInstanceRef.current = map;

        if (paths.length >= 3) {
            const polygon = new google.maps.Polygon({
                paths,
                strokeColor: '#2e7d32',
                strokeOpacity: 0.9,
                strokeWeight: 2,
                fillColor: '#66bb6a',
                fillOpacity: 0.3,
            });
            polygon.setMap(map);
            polygonRef.current = polygon;
        }

        paths.forEach((path, index) => {
            const content = document.createElement('div');
            content.style.cssText =
                'width:22px;height:22px;border-radius:50%;background-color:#2e7d32;border:2px solid white;box-shadow:0 2px 4px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:10px;';
            content.textContent = String(index + 1);
            new google.maps.marker.AdvancedMarkerElement({ position: path, map, content });
        });

        map.fitBounds(bounds, 30);

        return () => {
            if (polygonRef.current) polygonRef.current.setMap(null);
        };
    }, [coordinates]);

    return <div ref={mapRef} style={{ height, width: '100%', borderRadius: 8 }} />;
};

const MapWrapper: React.FC<{ coordinates: Coordinate[]; height: number }> = ({ coordinates, height }) => (
    <Wrapper
        apiKey={(import.meta as any).env?.VITE_MAP_API_KEY || 'AIzaSyA2-gtjUXGbaFwbFy8pmjTPgoV4pTLbx2g'}
        libraries={['marker']}
        render={(status) => {
            if (status === Status.FAILURE)
                return (
                    <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100', borderRadius: 1 }}>
                        <Typography variant="caption" color="error">Erro ao carregar mapa</Typography>
                    </Box>
                );
            return (
                <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">Carregando mapa…</Typography>
                </Box>
            );
        }}
    >
        <ReadOnlyPolygonMap coordinates={coordinates} height={height} />
    </Wrapper>
);

const SectionLabel: React.FC<{ icon: React.ReactNode; text: string; align?: 'left' | 'center' }> = ({ icon, text, align = 'left' }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: align === 'center' ? 'center' : 'flex-start', mb: 1.5 }}>
        {icon}
        <Typography variant="caption" fontWeight={700} sx={{ color: GREEN, letterSpacing: '1px' }}>
            {text}
        </Typography>
    </Box>
);

export const UniqueCodeRuleCard: React.FC<UniqueCodeRuleCardProps> = ({
    code,
    chainCode,
    giftId,
    useManualCode,
    useQrCode,
    requireLocation,
    locationCoordinates,
}) => {
    const notify = useNotify();
    const [mapOpen, setMapOpen] = useState(false);

    // Universal link do brinde (mesmo padrão do convite de grupo de clientes).
    // É ele — e não o giftId cru — que vai dentro do QR Code.
    const universalLink = buildGiftUniversalLink(chainCode, giftId, requireLocation);
    const qrValue = universalLink || giftId || '';

    const showManual = useManualCode !== false && !!code;
    const showQr = !!useQrCode && !!qrValue;
    const coords = locationCoordinates || [];
    const showLocation = !!requireLocation && coords.length > 0;

    const methodSummary = showManual && showQr ? 'Digitação ou QR Code' : showQr ? 'QR Code' : showManual ? 'Digitação' : '—';

    const handleCopy = () => {
        if (!code) return;
        navigator.clipboard?.writeText(code);
        notify('Código copiado', { type: 'info' });
    };

    const handleCopyLink = () => {
        if (!qrValue) return;
        navigator.clipboard?.writeText(qrValue);
        notify('Link copiado', { type: 'info' });
    };

    return (
        <Box sx={{ width: '100%' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: GREEN_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ConfirmationNumberOutlinedIcon sx={{ color: GREEN, fontSize: 22 }} />
                    </Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                        Divulgação
                    </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                    {methodSummary}
                </Typography>
            </Box>

            {(showManual || showQr) && <Divider />}

            {/* Métodos: Digitação + QR Code */}
            {(showManual || showQr) && (
                <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                    {showManual && (
                        <Box sx={{ flex: 1, p: 2 }}>
                            <SectionLabel icon={<KeyboardOutlinedIcon sx={{ color: GREEN, fontSize: 18 }} />} text="CÓDIGO ÚNICO" />
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1,
                                    bgcolor: '#fafafa',
                                    px: 2,
                                    py: 1.5,
                                }}
                            >
                                <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '1.25rem', letterSpacing: '3px' }}>
                                    {code}
                                </Typography>
                                <Tooltip title="Copiar código">
                                    <IconButton size="small" onClick={handleCopy}>
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                Cliente digita este código no app.
                            </Typography>
                        </Box>
                    )}

                    {showManual && showQr && <Divider orientation="vertical" flexItem />}

                    {showQr && (
                        <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <SectionLabel icon={<QrCode2Icon sx={{ color: GREEN, fontSize: 18 }} />} text="QR CODE" align="center" />
                            <Box sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fff' }}>
                                <QRCodeSVG value={qrValue} size={120} level="M" />
                            </Box>
                            <Button
                                size="small"
                                startIcon={<ContentCopyIcon fontSize="small" />}
                                onClick={handleCopyLink}
                                sx={{ mt: 1, color: GREEN }}
                            >
                                Copiar link
                            </Button>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>
                                Leitura pela câmera.
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {/* Localização obrigatória */}
            {showLocation && (
                <>
                    <Box sx={{ bgcolor: '#f5f5f5', p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ position: 'relative', width: 200, flexShrink: 0 }}>
                            <MapWrapper coordinates={coords} height={120} />
                            <Chip
                                icon={<LayersOutlinedIcon sx={{ fontSize: 14 }} />}
                                label="Geofence"
                                size="small"
                                sx={{ position: 'absolute', bottom: 6, left: 6, bgcolor: 'rgba(255,255,255,0.95)', height: 22, fontSize: '0.7rem' }}
                            />
                            <Button
                                size="small"
                                startIcon={<OpenInFullIcon sx={{ fontSize: 14 }} />}
                                onClick={() => setMapOpen(true)}
                                sx={{
                                    position: 'absolute',
                                    top: 6,
                                    right: 6,
                                    minWidth: 0,
                                    p: 0.5,
                                    height: 24,
                                    fontSize: '0.7rem',
                                    bgcolor: 'rgba(255,255,255,0.95)',
                                    color: GREEN,
                                    '&:hover': { bgcolor: '#fff' },
                                    // Só ícone por padrão; o texto expande no hover para
                                    // não cobrir o polígono do mapa.
                                    '& .MuiButton-startIcon': { m: 0 },
                                    '& .label': {
                                        maxWidth: 0,
                                        opacity: 0,
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                        transition: 'max-width 0.2s ease, opacity 0.2s ease, margin-left 0.2s ease',
                                    },
                                    '&:hover .label': { maxWidth: 60, opacity: 1, ml: 0.5 },
                                }}
                            >
                                <Box component="span" className="label">Ampliar</Box>
                            </Button>
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                                <LocationOnIcon sx={{ color: '#d32f2f', fontSize: 20 }} />
                                <Typography variant="caption" fontWeight={700} sx={{ color: '#d32f2f', letterSpacing: '0.5px' }}>
                                    LOCALIZAÇÃO OBRIGATÓRIA
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
                                {coords.map((c, i) => (
                                    <Box key={i} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                                        <Typography variant="caption" fontWeight={700} color="text.secondary">
                                            P{i + 1}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                            {c.latitude.toFixed(5)}, {c.longitude.toFixed(5)}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                Polígono de {coords.length} {coords.length === 1 ? 'ponto' : 'pontos'}
                            </Typography>
                        </Box>
                    </Box>

                    <Dialog open={mapOpen} onClose={() => setMapOpen(false)} maxWidth="md" fullWidth>
                        <DialogTitle>Área de resgate</DialogTitle>
                        <DialogContent>
                            <MapWrapper coordinates={coords} height={460} />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setMapOpen(false)}>Fechar</Button>
                        </DialogActions>
                    </Dialog>
                </>
            )}

            <Divider />

            {/* Footer */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, p: 1.5, px: 2 }}>
                <VisibilityOffOutlinedIcon sx={{ color: 'text.disabled', fontSize: 18 }} />
                <Typography variant="caption" color="text.secondary">
                    Brinde <strong>invisível</strong> na lista do Clube.
                </Typography>
            </Box>
        </Box>
    );
};
